import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_BASE_URL } from "../config/apiconfig";


const Sidebar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState("/employees");
    const [userDesignation, setUserDesignation] = useState("");
    const [companyName, setCompanyName] = useState("");
    const { user, userType, logout, loading } = useAuth();
    const navigate = useNavigate();
    const isAdmin = userType === "user"; // Admin if userType is 'user', employee otherwise
    
    const isAuthenticated = !!user && !!userType;

    useEffect(() => {
        // Set user designation based on userType and user data
        if (user && userType) {
            if (isAdmin) {
                setUserDesignation("Admin");
            } else {
                setUserDesignation(user.designation || "Employee");
            }
            
            // Fetch company name
            fetchCompanyName();
        } else {
            setUserDesignation("");
        }
    }, [user, isAdmin, userType]);

    const fetchCompanyName = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/auth/company-name`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'Authorization': `Bearer ${token}`
                }
            });
            setCompanyName(response.data.companyName);
        } catch (error) {
            console.error("Error fetching company name:", error);
        }
    };

    const handleToggleSidebar = () => {
        setIsOpen(!isOpen);
    };

    const handleLinkClick = (path) => {
        if (!path.startsWith('http')) {
            setSelected(path);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/");
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    const renderLink = (link) => {
        const isExternal = link.path.startsWith('http');
        
        if (!isAdmin && !['leave', 'attendance', 'training', 'profile'].includes(link.name.toLowerCase()) && !isExternal) {
            return null;
        }

        const commonClasses = `block py-3 px-4 
            ${selected === link.path ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'} 
            hover:bg-blue-500 hover:text-white rounded-md shadow-md transition-colors duration-300 
            font-semibold`;

        return isExternal ? (
            <a
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleLinkClick(link.path)}
                className={commonClasses}
                key={link.path}
            >
                {link.name}
            </a>
        ) : (
            <Link
                to={link.path}
                onClick={() => handleLinkClick(link.path)}
                className={commonClasses}
                key={link.path}
            >
                {link.name}
            </Link>
        );
    };

    if (loading) {
        return (
            <div className="sidebar w-64 bg-gradient-to-b from-gray-800 to-black text-white h-screen p-6 shadow-lg flex flex-col flex-shrink-0 overflow-y-auto">
                <div className="text-center mb-8">
                    <div className="mb-3">
                        <h1 className="text-lg font-bold text-blue-400 tracking-wide">E2E HRMS</h1>
                    </div>
                </div>
                
                <div className="flex-grow flex flex-col items-center justify-center">
                    <div className="bg-gray-700 p-6 rounded-lg shadow-lg text-center">
                        <p className="text-xl">Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="sidebar w-64 bg-gradient-to-b from-gray-800 to-black text-white h-screen p-6 shadow-lg flex flex-col flex-shrink-0 overflow-y-auto">
                <div className="text-center mb-8">
                    <div className="mb-3">
                        <h1 className="text-lg font-bold text-blue-400 tracking-wide">E2E HRMS</h1>
                    </div>
                </div>
                
                <div className="flex-grow flex flex-col items-center justify-center">
                    <div className="bg-gray-700 p-6 rounded-lg shadow-lg text-center">
                        <h3 className="text-xl font-bold mb-4">Access Required</h3>
                        <p className="mb-6">Please login through your account to access the system.</p>
                        <Link 
                            to="/" 
                            className="py-3 px-6 bg-blue-600 text-white hover:bg-blue-700 rounded-md shadow-md transition-all duration-300 font-semibold"
                        >
                            Login
                        </Link>
                    </div>
                </div>
                
                <div className="mt-auto text-center text-sm text-gray-300">
                    <p>&copy; 2024 HR Management</p>
                    <p>All Rights Reserved</p>
                </div>
            </div>
        );
    }

    const navLinks = [
        ...(isAdmin ? [] : [{ name: 'Profile', path: '/profile' }]),
        { name: 'Employees', path: '/create-employee' },
        { name: 'Company', path: '/company' },
        { name: 'Attendance', path: '/attendance' },
        { name: 'Leave', path: '/leave' },
        { name: 'Employee Benefits', path: '/benefits' },
        { name: 'Salary Calculator', path: '/salary-manager' },
        { name: 'Training', path: '/training' },
        { name: 'Hire Employees', path: '/hire' },
        { name: 'Resignations', path: '/resignations' },
        { name: 'Talent Pool', path: 'https://e2e.valuecraftminds.com' },
    ];

    return (
        <div className={`sidebar ${isOpen ? 'w-64' : 'w-16'} 
            lg:w-64 bg-gradient-to-b from-gray-800 to-black text-white h-screen p-6 shadow-lg flex flex-col flex-shrink-0 overflow-y-auto transition-all duration-300`}>
            
            {/* Hamburger Menu for Mobile */}
            <div className="flex justify-between items-center">
                <button
                    onClick={handleToggleSidebar}
                    className="lg:hidden text-white focus:outline-none"
                >
                    <span className="block w-6 h-1 bg-white mb-2"></span>
                    <span className="block w-6 h-1 bg-white mb-2"></span>
                    <span className="block w-6 h-1 bg-white"></span>
                </button>
            </div>

            {/* Logo and Company Info Section */}
            <div className={`text-center mb-4 ${isOpen ? '' : 'hidden'} lg:block`}>
                <Link to="/dashboard" className="block">
                    <div className="mb-3 p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg">
                        <h1 className="text-lg font-bold text-white tracking-wide mb-1">E2E HRMS</h1>
                        {companyName && (
                            <div className="border-t border-blue-300 pt-1">
                                <p className="text-sm font-semibold text-blue-100 tracking-wide">
                                    {companyName}
                                </p>
                            </div>
                        )}
                    </div>
                </Link>
                
                {user && (
                    <div className="mt-3 p-3 bg-gray-700 rounded-lg">
                        <p className="font-medium text-sm">{user.name || user.fullName || "User"}</p>
                        <p className="text-xs text-gray-300">{userDesignation}</p>
                    </div>
                )}
            </div>

            {/* Navigation Links */}
            <nav className={`flex-grow ${isOpen ? '' : 'hidden'} lg:block`}>
                <ul className="space-y-2">
                    {navLinks.map((link) => (
                        <li key={link.path}>{renderLink(link)}</li>
                    ))}
                </ul>
            </nav>

            {/* Footer Section with Logout */}
            <div className={`mt-auto ${isOpen ? '' : 'hidden'} lg:block`}>
                <button
                    onClick={handleLogout}
                    className="w-full py-3 px-4 bg-red-600 text-white hover:bg-red-700 rounded-md shadow-md transition-all duration-300 font-semibold mb-3"
                >
                    Logout
                </button>
                <div className="text-center text-sm text-gray-300">
                    <p>&copy; 2024 HR Management</p>
                    <p>All Rights Reserved</p>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;